import { combineReducers, configureStore, Reducer, UnknownAction } from '@reduxjs/toolkit';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist';

import { apiServices } from 'services';
import { aiAssistantChatApi } from 'services/aiAssistantChat/aiAssistantChatApi';
import { marketoneApi } from 'services/marketone/marketone';

import { IS_LIVE } from 'utils/constants';
import { clearStaffViewAsPatient, getBrowserLocalStorage } from 'utils/storage';

// Imported from the leaf modules rather than the feature barrel: the barrel re-exports
// ZoomChatSlot, which reaches ZoomChatWidget, which imports this store, a cycle. All three of
// these depend on nothing but the feature's constants, so none of them reaches back here.
import { clearZoomChatIdentity } from 'features/ZoomChat/clearZoomChatIdentity';
import { revokeZoomChatIdentity } from 'features/ZoomChat/zoomChatIdentityRevocation';
import { endZoomChatEngagement } from 'features/ZoomChat/zoomChatSdk';

import { createClearStaffViewAsPatientMiddleware } from './middleware/clearStaffViewAsPatientMiddleware';
import {
  createClearUserPersistAfterMiddleware,
  PERSIST_ROOT_KEY
} from './middleware/clearUserPersistAfterMiddleware';
import { createClearZoomChatIdentityMiddleware } from './middleware/clearZoomChatIdentityMiddleware';
import { createRtkQueryErrorLogger } from './middleware/rtkQueryErrorLogger';
import {
  authPersistTransform,
  syncAuthTokensToSessionMiddleware
} from './persistence/authSessionPersistence';
import { sliceReducers } from './slices';

type PersistStorage = {
  getItem: (key: string) => Promise<string | null>;
  removeItem: (key: string) => Promise<void>;
  setItem: (key: string, value: string) => Promise<string>;
};

const createNoopStorage = (): PersistStorage => ({
  getItem: async () => null,
  removeItem: async () => undefined,
  setItem: async (_key: string, value: string) => value
});

const createWebStorage = (): PersistStorage => ({
  getItem: async (key: string) => {
    try {
      return getBrowserLocalStorage()?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  removeItem: async (key: string) => {
    try {
      getBrowserLocalStorage()?.removeItem(key);
    } catch {
      // storage unavailable (SecurityError, private mode, blocked third-party)
    }
  },
  setItem: async (key: string, value: string) => {
    try {
      getBrowserLocalStorage()?.setItem(key, value);
    } catch {
      // storage unavailable — continue without persisting (same as noop storage)
    }
    return value;
  }
});

const storage = typeof window !== 'undefined' ? createWebStorage() : createNoopStorage();

/**
 * API slices whose failures must not raise the global error toast. Their data feeds surfaces that
 * are absent for the vast majority of patients — the MarketONE entitlements lookup runs on the
 * billing page and in the appointment flow for everyone, but only AT&T patients have an AT&T
 * section to show — so a 500 there would put a server error in front of people with nothing at
 * stake. The AI assistant chat's own lookups (prescriptions and shop orders for the greeting's
 * order picks) are silent for the same reason: the platform answers `/store/orders` with a 500 for
 * patients without shop orders. The action still rejects, so consumers keep RTK Query's error
 * state and its last successful payload; only the toast is skipped.
 */
export const SILENT_ERROR_REDUCER_PATHS: string[] = [
  aiAssistantChatApi.reducerPath,
  marketoneApi.reducerPath
];

const rtkQueryErrorLogger = createRtkQueryErrorLogger(SILENT_ERROR_REDUCER_PATHS);

const persistConfig = {
  key: 'root',
  storage,
  transforms: [authPersistTransform],
  whitelist: [
    'user',
    'signUp',
    'provider',
    'appointments',
    'mif',
    'renewal',
    'orchestrate',
    'join',
    'programs',
    'programsCatalog'
  ]
};

type ApiReducerMap = {
  [Api in (typeof apiServices)[number] as Api['reducerPath']]: Api['reducer'];
};

const apiReducers = Object.fromEntries(
  apiServices.map((api) => [api.reducerPath, api.reducer])
) as ApiReducerMap;

const apiMiddlewares = apiServices.map((api) => api.middleware);

const appReducer = combineReducers({
  ...sliceReducers,
  ...apiReducers
});

const clearUserPersistAfterMiddleware = createClearUserPersistAfterMiddleware(() =>
  storage.removeItem(PERSIST_ROOT_KEY)
);

// Zoom keeps its consumer identity in localStorage, outside both Redux and redux-persist, so
// clearing the portal session does not clear it — and the engagement itself lives on Zoom's side,
// where nothing we clear locally reaches. Attached here rather than at the twenty-plus `clearUser`
// call sites so no future session-clearing path can forget either half, or the revocation of the
// credential hook that has to land before either of them.
const clearZoomChatIdentityMiddleware = createClearZoomChatIdentityMiddleware(
  clearZoomChatIdentity,
  endZoomChatEngagement,
  revokeZoomChatIdentity
);

// The tab's staff-viewing-as-a-patient context lives in sessionStorage, outside Redux, and most
// `clearUser` call sites end a session without clearing it. Attached here for the same reason as
// the Zoom identity above, so the patient who signs in next does not inherit a staff context.
const clearStaffViewAsPatientMiddleware =
  createClearStaffViewAsPatientMiddleware(clearStaffViewAsPatient);

const rootReducer: Reducer = (state: RootState, action: UnknownAction) => {
  if (action.type === 'user/clearUser') {
    state = {} as RootState;
  }
  return appReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  devTools: !IS_LIVE,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    }).concat([
      ...apiMiddlewares,
      rtkQueryErrorLogger,
      syncAuthTokensToSessionMiddleware,
      clearUserPersistAfterMiddleware,
      clearZoomChatIdentityMiddleware,
      clearStaffViewAsPatientMiddleware
    ]),
  reducer: persistedReducer
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof appReducer>;
export type AppDispatch = typeof store.dispatch;

export { selectAppointments, selectNewAppointment } from './appointments/appointmentsSlice';
export { selectHealthProfile } from './healthProfile/healthProfileSlice';
export { selectJoin } from './join/joinSlice';
export { selectMifInfo } from './mif/mifSlice';
export { selectModalState } from './modals/modalSlice';
export {
  selectGroupedNotifications,
  selectHasMore,
  selectHasUnreadNotifications,
  selectHighPriorityUnreadCount,
  selectIsError,
  selectIsLoading,
  selectIsLoadingMore,
  selectPaginatedCount,
  selectTotalCount,
  selectNotificationsList,
  selectShouldAnimateIndicator
} from './notifications/notificationsSlice';
export { selectOrchestrate } from './orchestrate-signup/orchestrateSlice';
export { selectRenewalInfo } from './renewal/renewalSlice';
export { selectNotificationPreferences } from './settings/settingsSlice';
export { selectShop } from './shop/shopSlice';
export {
  selectSelectedPricePoint,
  selectSignUp,
  selectStatesWhereAshIsAvailable
} from './signup/signupSlice';
export { selectUser, selectWriteMessageFeatureStatus } from './user/userSlice';
export { selectWmPages, selectWmSections } from './wm/wmSlice';
